import { useState, type SyntheticEvent } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { redirectIfAuthenticated } from '@/lib/auth-guards';
import { authClient } from '@/lib/auth-client';
import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { TextField } from '@/components/ui/Field';
import { AuthLayout } from '@/features/auth/AuthLayout';

export const Route = createFileRoute('/forgot-password')({
  beforeLoad: () => redirectIfAuthenticated(),
  component: ForgotPasswordPage,
});

function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  async function onSubmit(event: SyntheticEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setError(null);
    setPending(true);
    const { error: requestError } = await authClient.requestPasswordReset({
      email,
      redirectTo: `${window.location.origin}/reset-password`,
    });
    setPending(false);
    if (requestError) {
      setError('We could not send the email. Check your connection and try again.');
      return;
    }
    // Same answer whether or not the address has an account.
    setSent(true);
  }

  return (
    <AuthLayout
      title="Forgot your password?"
      description="Enter your email and we will send you a link to choose a new one."
      footer={
        <Link to="/sign-in" className="font-bold text-primary hover:underline">
          Back to log in
        </Link>
      }
    >
      {sent ? (
        <Alert tone="success" title="Check your email.">
          If there is an account for {email}, we have sent a link. It works for one hour. Your
          practice history and results stay exactly as they are.
        </Alert>
      ) : (
        <form className="grid gap-4" onSubmit={(event) => void onSubmit(event)}>
          {error ? <Alert tone="danger" title={error} /> : null}

          <TextField
            label="Email"
            type="email"
            required
            autoComplete="email"
            value={email}
            onChange={(event) => {
              setEmail(event.target.value);
            }}
          />

          <Button type="submit" size="lg" fullWidth loading={pending}>
            Send reset link
          </Button>
        </form>
      )}
    </AuthLayout>
  );
}
